import { FC, useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CardAction } from '@hdi-seguros/web-components.card/dist/card';

import { PolicyCard } from '../../../../../shared/components';

import { EMPTY_TEXT, encrypt, formatDate, formattedPrice, getTagsByStates } from '../../../../../shared/utils';

import { InsurancePolicy, PolicyStates, DECISION_TYPE, PaymentMethod } from '../../../../../shared/interfaces';
import { CancelPolicyModal } from '../../../InsurancePolicyDetail/components/CancelPolicyModal';
import { RevertEndorsementModal } from '../../../InsurancePolicyDetail/components/RevertEndorsementModal';
import { PaymentFormatModal } from '../../../InsurancePolicyDetail/components/PaymenFormatModal';
import { QuotationGroups } from '../../../../quotation/NewQuote/containers';
import { RenewalQuotesContext } from '../../../../quotation/SavedQuotes/shared/contexts/RenewalQuotes';
import { RenewAPModal } from '../../../InsurancePolicyDetail/components/RenewApModal';
import { Branches } from '../../../../../shared/interfaces/services/branches-enum';

interface InsurancePolicyCardProps {
  insurancePolicy: InsurancePolicy;
  loading?: boolean;
  onUpdate: () => Promise<void>;
}

export const InsurancePolicyCard: FC<InsurancePolicyCardProps> = ({
  insurancePolicy,
  loading,
  onUpdate,
}) => {
  const { 
    getQuotationGroupByArticle,
  } = useContext(RenewalQuotesContext);

  const navigate = useNavigate();
  const [ showReverseEndorsementModal, setShowReverseEndorsementModal ] = useState<boolean>(false);
  const [ showPaymentFormatModal, setShowPaymentFormatModal ] = useState<boolean>(false);

  const {
    nombreAsegurado,
    nombreProductor,
    fechaVigDesd,
    fechaVigHast,
    fechaEmision,
    moneda,
    prima,
    premio,
    nroPoliza,
    nombreArticulo,
    estados,
    rama,
    descripcionRama,
    articulo,
    permiteRenovar,
    superpoliza,
    tieneEndosoParaRevertir
  } = insurancePolicy;

  const [ showCancelationModal, setShowCancelationModal ] = useState<boolean>(false);
  const [ showRenewApModal, setShowRenewApModal ] = useState<boolean>(false);

  const onPressRenewPolicy = useCallback(() => renewPolicy(), []);

  const renewPolicy = () => {    
    if(insurancePolicy.rama === Branches.ACCIDENTES_PERSONALES_COLECTIVO){
      setShowRenewApModal(true);
    } else {
      onRenewQuote(insurancePolicy.nroPoliza, insurancePolicy.articulo, insurancePolicy.superpoliza);
    }
  };

  const onRenewQuote = useCallback((quotationNumber: string, article: string, superQuotationNumber: string) => {       
    const groupSelected = getQuotationGroupByArticle(article);
    const quotationInfo = { quotationNumber, groupSelected, article, superQuotationNumber };
    const parseQuoteInfo = encrypt<{ quotationNumber: string, groupSelected: QuotationGroups, article: string, superQuotationNumber : string }>(quotationInfo);
    navigate(`/cotizador/renovar-cotizacion/${parseQuoteInfo}`);
  }, [navigate]);

  const onPressCancelPolicy = useCallback(() => setShowCancelationModal(true), [setShowCancelationModal]);

  const onPressViewPolicy = useCallback(() =>
    navigate(
      `/produccion/detalle-de-poliza/${nroPoliza}?branch=${rama}&article=${articulo}&superpolicy=${superpoliza}`,
      {state : {policyFromList : insurancePolicy}}
    ),
  [navigate, nroPoliza, rama, articulo, superpoliza,insurancePolicy]
);
  const onCloseModal = useCallback(
    () => {
      setShowCancelationModal(false);
      setShowRenewApModal(false);
    }, 
    [navigate]
  );

  const onCloseModalAndUpdate = useCallback(
    () => {
      setShowCancelationModal(false);
      setShowRenewApModal(false);
      onUpdate();
    }, 
    [navigate]
  );

  const openRevertEndorsement =useCallback( () => setShowReverseEndorsementModal(true), [setShowReverseEndorsementModal]);
  const openPaymentFormatModal =useCallback( () => setShowPaymentFormatModal(true), [setShowPaymentFormatModal]);

  const updatePaymentFormat = (paymentFormat: PaymentMethod) =>{
    insurancePolicy.formaDePago = paymentFormat;
  } 

  const cardActions = useMemo(
    () => {
      const actions: Array<CardAction> = [
        {
          label: 'Ver póliza',
          onClick: onPressViewPolicy,
        },
      ];
      const RENEWABLE_BRANCHES = [Branches.AUTOMOVILES, Branches.COMBINADO_FAMILIAR, Branches.INTEGRAL_CONSORCIO];
      const EXCLUDED_AUTOS_ARTICLES = ['1006', '1093'];
      const isRenewableBranch = RENEWABLE_BRANCHES.includes(rama as Branches);
      const isExcludedAutosArticle = rama === Branches.AUTOMOVILES && EXCLUDED_AUTOS_ARTICLES.includes(articulo);
      if(isRenewableBranch && !isExcludedAutosArticle && (estados?.includes(PolicyStates.IS_RENEWAL) && permiteRenovar === DECISION_TYPE.YES)) {
        actions.push({
          label: 'Renovar póliza',
          onClick: onPressRenewPolicy
        });
      };
      
      if(tieneEndosoParaRevertir) {
        if(tieneEndosoParaRevertir.includes(DECISION_TYPE.YES)){
          actions.push({
            label: 'Revertir endoso', 
            onClick: openRevertEndorsement
          });
        }
      };

      if(parseInt(insurancePolicy?.saldoPendiente!) > 0) {
        actions.push({
            label: 'Cambiar forma pago',
            onClick: openPaymentFormatModal,
        })
    };

      if(estados?.includes(PolicyStates.ACTIVE) && !estados?.includes(PolicyStates.CANCELED)) {
        actions.push({
          label: 'Anular póliza', 
          onClick: onPressCancelPolicy
        });
      };

      return actions;
    },
    [
      estados, 
      tieneEndosoParaRevertir,
      permiteRenovar, 
      onPressViewPolicy,
      onPressRenewPolicy,
    ]
  );

  const updateRevertEndorsement = () => {
    insurancePolicy.tieneEndosoParaRevertir = DECISION_TYPE.NO
  }

  const columnsDataPolicy = [
    {
      label: 'Póliza',
      value: nroPoliza ?? EMPTY_TEXT,
      isLikeTitle: true,
      valueInBold: true,
      columnSize: { xs: { span: 12 }, md: { span: 6 } },
    },
    {
      label: 'Asegurado',
      value: nombreAsegurado ?? EMPTY_TEXT,
      valueInBold: true,
      columnSize: { xs: { span: 12 }, md: { span: 6 } },
    },
    {
      label: 'Productor',
      value: nombreProductor ?? EMPTY_TEXT,
      columnSize: { xs: { span: 12 }, md: { span: 6 } },
    },
    {
      label: 'F. Emisión',
      value: fechaEmision ? formatDate(fechaEmision) : EMPTY_TEXT,
      columnSize: { xs: { span: 12 }, md: { span: 6 } },
    },
    {
      label: 'Inicio de la póliza',
      value: fechaVigDesd ? formatDate(fechaVigDesd) : EMPTY_TEXT,
      columnSize: { xs: { span: 12 }, md: { span: 6 } },
    },
    {
      label: 'Fin de la póliza',
      value: fechaVigHast ? formatDate(fechaVigHast) : EMPTY_TEXT,
      columnSize: { xs: { span: 12 }, md: { span: 6 } },
    },
    {
      label: 'Prima',
      value: prima && moneda ? `${moneda} ${formattedPrice(prima)}` : EMPTY_TEXT,
      columnSize: { xs: { span: 12 }, md: { span: 6 } },
    },
    {
      label: 'Premio',
      value: moneda && premio ? `${moneda} ${formattedPrice(premio)}` : EMPTY_TEXT,
      columnSize: { xs: { span: 12 }, md: { span: 6 } },
    },
  ];
  
  const tags = useMemo(() => getTagsByStates(estados), [getTagsByStates, estados]);

  return (
    <div>
      <PolicyCard
        title={descripcionRama ?? EMPTY_TEXT}
        article={nombreArticulo || articulo}
        tags={tags}
        actions={cardActions}
        loading={loading}
        columnsData={columnsDataPolicy}
      />
    
      {
        showReverseEndorsementModal && (
          <RevertEndorsementModal
            title={'Renovar Endoso'} 
            open={ showReverseEndorsementModal } 
            insurancePolicy={ insurancePolicy }
            onClose={() => setShowReverseEndorsementModal(false)}
            onConfirm={ updateRevertEndorsement }
          />
        )
      }
      {
        showPaymentFormatModal && (
          <PaymentFormatModal
            title={`Cambiar forma de pago`} 
            open={ showPaymentFormatModal } 
            insurancePolicy={ insurancePolicy }
            onClose={() => setShowPaymentFormatModal(false)}
            onConfirm={ updatePaymentFormat }
          />
        )
      }
      {
        showCancelationModal && (
          <CancelPolicyModal
            title={`Anulación de póliza electrónica`} 
            open={ showCancelationModal } 
            insurancePolicy={ insurancePolicy }
            onClose={onCloseModal}
            onUpdate={onCloseModalAndUpdate}
          />
        )
      }
      {
        showRenewApModal && (
          <RenewAPModal
            title={`Renovación de Póliza: ${insurancePolicy.nroPoliza} - ${insurancePolicy.descripcionRama}`} 
            open={ showRenewApModal } 
            insurancePolicy={ insurancePolicy }
            onClose={onCloseModal}
            onUpdate={onCloseModalAndUpdate}
          />
        )
      }
    </div>
  );
};
